// Pure helpers behind the Tasks <-> Client Tickets link (Nikhil 2026-09-12,
// refinement A) and the "Log call / meeting" affordance on a task (refinement
// B). Exercised by task_ticket_links.test.js without a backend.
//
// cs-analysis contract (built in parallel — every consumer is defensive):
//   POST /api/v1/clients/:storeId/actionables/from-client-ticket/:ticketId
//        { title?, description?, module?, owner?, dueDate? } -> actionable view
//        (400 "module required" when the ticket's module is OTHER and none given;
//        returns the existing open task if one already links the ticket)
//   POST /api/v1/clients/:storeId/actionables/:id/link-client-ticket   { ticketId }
//   POST /api/v1/clients/:storeId/actionables/:id/unlink-client-ticket { ticketId }
//   actionable view += linkedClientTickets: [{ id, ref, title, status, priority, module }]
//   ticket detail   += linkedActionables:   [{ id, title, status, owner, dueDate }]

// Task modules the from-client-ticket endpoint accepts (no CLIENT_ORG — a
// ticket is always about a product module; MES / OTHER tickets need a pick).
export const TASK_MODULES = ['DESIGN', 'RENDER', 'PRESENTATION', 'CATALOGUE', 'PRICING', 'PRODUCTION'];

// Client-ticket status palette (same values as ClientComms / the queue).
export const CLIENT_TICKET_STATUS_COLORS = {
    OPEN: '#e67e22', IN_PROGRESS: '#2f6fed', WAITING_ON_CLIENT: '#8e44ad',
    WAITING_ON_PRODUCT: '#16a085', RESOLVED: '#27ae60', CLOSED: '#7f8c8d', DECLINED: '#c0392b',
};

// "Open" for linking purposes = the same set the Client Tickets "Active"
// filter shows (everything not CLOSED / DECLINED). A RESOLVED ticket is still
// linkable — a rep may want a verification task on it before it auto-closes.
const CLOSED_TICKET_STATUSES = ['CLOSED', 'DECLINED'];
export const is_open_client_ticket = (t) => !!t && !CLOSED_TICKET_STATUSES.includes(t.status);

// Task statuses that can still take new links (DEFERRED = snoozed, still live).
const CLOSED_TASK_STATUSES = ['DONE', 'DISMISSED'];
export const is_open_task = (r) => !!r && !CLOSED_TASK_STATUSES.includes(r.status);

// Title Case for enum display (matches ClientComms' label()).
export const enum_label = (s) => (s || '').split('_')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : '')).join(' ');

const truncate = (s, n) => {
    const v = (s || '').toString();
    return v.length > n ? v.slice(0, n - 1) + '…' : v;
};

// Chip text on a task card: "#<ref> <title> · <status>".
export const client_ticket_chip_label = (t) => {
    if (!t) return '';
    const ref = t.ref !== undefined && t.ref !== null && t.ref !== '' ? `#${t.ref}` : '';
    const parts = [[ref, truncate(t.title, 60)].filter(Boolean).join(' '), t.status ? enum_label(t.status) : ''];
    return parts.filter(Boolean).join(' · ');
};

// One line per linked task in the ticket drawer: "title · status · owner · due <date>".
export const describe_linked_actionable = (a) => {
    if (!a) return '';
    const due = a.dueDate ? `due ${String(a.dueDate).slice(0, 10)}` : '';
    return [truncate(a.title || a.description, 80), a.status ? enum_label(a.status) : '', a.owner || '', due]
        .filter(Boolean).join(' · ');
};

// Where the ticket detail's linked tasks live. The contract puts
// linkedActionables on the ticket detail view; GET /tickets/:id wraps the
// ticket as { ticket, channelId, following } — accept either placement, and
// an older backend (no field at all) reads as "none linked".
export const linked_actionables_of = (detail) => {
    if (!detail || typeof detail !== 'object') return [];
    if (Array.isArray(detail.linkedActionables)) return detail.linkedActionables;
    if (detail.ticket && Array.isArray(detail.ticket.linkedActionables)) return detail.ticket.linkedActionables;
    return [];
};

// The client's store id for a ticket. The staff list rows carry
// client: { name, storeId }; the bare detail row carries only clientId (a
// cs-analysis internal id — NOT usable), so callers that know the org (the
// per-client tab) pass it explicitly.
export const ticket_store_id = (ticket, explicit) => {
    if (explicit) return String(explicit);
    if (!ticket) return null;
    if (ticket.client && ticket.client.storeId) return String(ticket.client.storeId);
    if (ticket.storeId) return String(ticket.storeId);
    return null;
};

// Seed for TaskCreateModal when a task is raised from a ticket. Module is
// carried only when it's one the task endpoint accepts — for MES / OTHER the
// rep must pick (the endpoint 400s otherwise), so the modal shows a hint.
export const task_prefill_from_ticket = (ticket) => {
    if (!ticket) return {};
    return {
        title: ticket.title || '',
        description: ticket.description || ticket.title || '',
        module: TASK_MODULES.includes(ticket.module) ? ticket.module : undefined,
    };
};

// The client's open tasks matching a free-text query (title / description),
// for the "Link existing task" picker in the ticket drawer. Tasks already
// linked to the ticket are dropped.
export const filter_linkable_tasks = (rows, q, already_linked_ids) => {
    const needle = (q || '').trim().toLowerCase();
    const linked = new Set((already_linked_ids || []).map(String));
    return (Array.isArray(rows) ? rows : [])
        .filter(is_open_task)
        .filter((r) => !linked.has(String(r.id)))
        .filter((r) => !needle || [r.title, r.description].some((f) => (f || '').toLowerCase().includes(needle)));
};

// The client's open tickets not yet linked to a task, filtered by query, for
// the task card's "client ticket" link picker.
export const filter_linkable_tickets = (tickets, q, already_linked_ids) => {
    const needle = (q || '').trim().toLowerCase();
    const linked = new Set((already_linked_ids || []).map(String));
    return (Array.isArray(tickets) ? tickets : [])
        .filter(is_open_client_ticket)
        .filter((t) => !linked.has(String(t.id)))
        .filter((t) => !needle || [t.title, `#${t.ref}`, `ct-${t.ref}`, t.module].some((f) => (f || '').toString().toLowerCase().includes(needle)));
};

// Prefill for AddEditActivity when a rep logs the call / meeting she did for
// a task: subject = the task title, note names the task, type = call. Shaped
// for the sessionStorage 'cs_task_prefill' channel AddEditActivity already
// reads (subject / note / org / due).
export const activity_prefill_from_task = (task, store_id) => {
    if (!task) return null;
    const title = (task.title || task.description || '').trim();
    const detail = task.title && task.description ? task.description.trim() : '';
    const note = ['Task: ' + title, detail].filter(Boolean).join('\n');
    return {
        subject: truncate(title, 120),
        note,
        org: store_id ? String(store_id) : '',
        type: 'call',
    };
};
